'use client';
import React, { useRef } from 'react';
import Image from 'next/image';
import { IKButton } from '@/components/shared/material';
import { PortableText } from '@portabletext/react';
import { PortableTextBlock } from 'sanity';
import { components } from './PortableTextComponents';
import { m, useInView } from 'framer-motion';
import { fadeUpLeft, fadeUp } from '@/assets/framer/animations';
import { MenuItemLink, TrustBadgeType } from '@/types/types';
import { InfiniteCarousel } from '@/components/slider';
import { ShoppingCart } from '@phosphor-icons/react';

import TrustBadge from '../shared/TrustBadge';
import { useRelativeCoordinates } from '@/hooks/useRelativeCoordinates';
import { testIds } from '@/constants/testIds';
function SVGPatterns({
  mousePosition,
  isInView,
}: {
  className?: string;
  mousePosition: any;
  isInView: boolean;
}) {
  return (
    <>
      {/* Floating patterns */}
      {/* Teal pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className='absolute -left-8 top-0 z-20 h-12 w-12 md:z-0 lg:-top-8 lg:h-16 lg:w-16'
        width='67'
        height='67'
        viewBox='0 0 67 67'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 50 - 10 : 0,
                y: mousePosition.y ? mousePosition.y / 50 - 10 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.8986 27.3153C27.1852 27.3153 27.4145 27.0858 27.4145 26.7989V14.655H39.9672V26.7989C39.9672 27.0858 40.1965 27.3153 40.4831 27.3153H52.176V39.8226H40.0436C39.757 39.8226 39.5278 40.0521 39.5278 40.339V52.7889H27.7202V40.3963C27.7202 40.1095 27.4909 39.88 27.2043 39.88H14.7471V27.3153H26.8604H26.8986ZM1.25824 40.6258H13.7154V53.4009C13.7154 53.6878 13.9447 53.9173 14.2313 53.9173H26.8986V66.3098C26.8986 66.5967 27.1279 66.8262 27.4145 66.8262H40.4066C40.6932 66.8262 40.9225 66.5967 40.9225 66.3098V53.8408H53.0549C53.3415 53.8408 53.5707 53.6113 53.5707 53.3244V40.5493H65.7031C65.9897 40.5493 66.219 40.3198 66.219 40.033V27.0475C66.219 26.7607 65.9897 26.5312 65.7031 26.5312H54.0102V13.8135C54.0102 13.5266 53.7809 13.2971 53.4943 13.2971H40.7314V1.13403C40.7314 0.847168 40.5022 0.617676 40.2156 0.617676H27.2425C26.9559 0.617676 26.7267 0.847168 26.7267 1.13403V13.2589H13.9638C13.6772 13.2589 13.4479 13.4884 13.4479 13.7752V26.5503H1.25824C0.971644 26.5503 0.742371 26.7798 0.742371 27.0667V40.0521C0.742371 40.339 0.971644 40.5685 1.25824 40.5685'
          fill='#009EAC'
        />
      </m.svg>
      {/* Orange pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className='absolute -left-24 top-10 z-0 h-8 w-8 lg:-left-28 lg:h-10 lg:w-10 xl:-left-32 xl:top-12'
        width='41'
        height='40'
        viewBox='0 0 41 40'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 30 + 10 : 0,
                y: mousePosition.y ? mousePosition.y / 30 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M26.4013 25.7783H14.2307V14.3611H26.4013V25.7783ZM0.703548 27.5378H13.3518V39.2801C13.3518 39.567 13.5811 39.7965 13.8677 39.7965H26.8407C27.1273 39.7965 27.3566 39.567 27.3566 39.2801V26.6007H39.8711C40.1576 26.6007 40.3869 26.3712 40.3869 26.0843V13.0798C40.3869 12.7929 40.1576 12.5634 39.8711 12.5634H27.2228V0.821045C27.2228 0.53418 26.9935 0.304688 26.707 0.304688H13.7339C13.4473 0.304688 13.218 0.53418 13.218 0.821045V13.5005H0.703548C0.416956 13.5005 0.187683 13.73 0.187683 14.0168V27.0023C0.187683 27.2892 0.416956 27.5186 0.703548 27.5186'
          fill='#D64C19'
        />
      </m.svg>
      {/* Pink pattern */}
      <m.svg
        initial='hidden'
        variants={fadeUpLeft}
        transition={{
          delay: mousePosition.x ? 0 : 0.3,
          type: 'tween',
        }}
        xmlns='http://www.w3.org/2000/svg'
        className='absolute top-16 z-20 h-28 w-28 sm:h-28 sm:w-28 md:-left-16 lg:-right-20 lg:h-40 lg:w-32 xl:-right-16 xl:top-20 xl:w-40'
        width='142'
        height='143'
        viewBox='0 0 142 143'
        fill='none'
        animate={{
          ...(isInView
            ? {
                opacity: 1,
                x: mousePosition.x ? mousePosition.x / 40 : 0,
                y: mousePosition.y ? mousePosition.y / 40 : 0,
              }
            : fadeUp.hidden),
        }}
      >
        <path
          d='M64.0003 53.6257C64.4779 53.6257 64.8601 53.2432 64.8601 52.7651V40.602C64.8601 40.602 64.8218 40.5637 64.8218 40.5255H76.8396V52.4973C76.8396 52.9754 77.2217 53.3771 77.7185 53.3771H89.4878V64.8708C89.4878 65.3489 89.889 65.7505 90.3667 65.7505H101.754V76.9H90.3667C89.889 76.9 89.4878 77.2825 89.4878 77.7797V89.2735H77.9095C77.4319 89.2735 77.0306 89.6559 77.0306 90.1532V102.297C77.0306 102.297 77.0689 102.355 77.0689 102.374H65.1084C65.1658 102.259 65.2422 102.163 65.2422 102.049V89.8854C65.2422 89.4073 64.8601 89.0057 64.3824 89.0057H52.8232V77.5885C52.8232 77.1104 52.4411 76.7279 51.9634 76.7279H40.2323V66.0756C40.2323 66.0756 40.175 65.9609 40.1559 65.9035H51.6768C52.1545 65.9035 52.5366 65.521 52.5366 65.0429V53.6257H64.0194H64.0003ZM14.4964 52.0001C14.0187 52.0001 13.6366 52.3826 13.6366 52.8607V65.0238C13.6366 65.0238 13.6939 65.1385 13.713 65.1959H1.69529C1.21763 65.1959 0.816406 65.5975 0.816406 66.0756V78.2196C0.816406 78.6977 1.19853 79.0993 1.69529 79.0993H13.9996V89.7516C13.9996 90.2297 14.3817 90.6122 14.8594 90.6122H26.1893V102.029C26.1893 102.508 26.5714 102.89 27.0491 102.89H39.0668C38.9522 103.062 38.8184 103.253 38.8184 103.464V115.13C38.8184 115.742 39.3152 116.258 39.9266 116.258H51.2947C51.2947 116.258 51.2565 116.296 51.2565 116.334V128.498C51.2565 128.976 51.6386 129.377 52.1354 129.377H64.1722V141.885C64.1722 142.171 64.4015 142.401 64.6881 142.401H77.5465C77.8331 142.401 78.0624 142.171 78.0624 141.885V129.377H89.7171C90.1947 129.377 90.596 128.976 90.596 128.498V116.334C90.596 116.334 90.5769 116.296 90.5769 116.258H102.442C103.053 116.258 103.55 115.761 103.55 115.13V103.464C103.55 103.33 103.454 103.253 103.416 103.139H115.529C116.007 103.139 116.408 102.756 116.408 102.259V90.7652H128.197C128.674 90.7652 129.076 90.3636 129.076 89.8854V78.4108H140.234C140.711 78.4108 141.113 78.0283 141.113 77.5311V65.368C141.113 64.8899 140.711 64.4883 140.234 64.4883H128.713V52.6694C128.713 52.1913 128.331 51.7897 127.834 51.7897H116.045V40.2769C116.045 39.7988 115.663 39.4163 115.166 39.4163H103.875C103.875 39.4163 103.913 39.3589 103.913 39.3398V27.693C103.913 27.0811 103.416 26.5647 102.786 26.5647H90.1374C90.1947 26.45 90.2712 26.3735 90.2712 26.2396V14.0765C90.2712 13.5984 89.8699 13.1968 89.3923 13.1968H77.6994V0.746826C77.6994 0.459961 77.4701 0.230469 77.1835 0.230469H64.3251C64.0385 0.230469 63.8092 0.459961 63.8092 0.746826V13.2542H51.7724C51.2947 13.2542 50.8935 13.6366 50.8935 14.1339V26.297C50.8935 26.4308 50.989 26.5073 51.0272 26.6221H39.4298C38.7993 26.6221 38.3026 27.1193 38.3026 27.7504V39.3971C38.3026 39.531 38.3981 39.6075 38.4363 39.7223H26.6479C26.1702 39.7223 25.769 40.1047 25.769 40.5829V52.0001H14.4391H14.4964Z'
          fill='#D72A4E'
        />
      </m.svg>
    </>
  );
}

export default function HeroBanner(props: {
  data?: {
    sectionId?: string;
    heading?: PortableTextBlock;
    subheading?: PortableTextBlock;
    buttons?: [
      {
        text?: string;
        link: MenuItemLink;
      },
    ];
    images?: {
      id?: string;
      url?: string;
      alt?: string;
      mediaAlt?: string;
      blurDataUrl?: string;
    }[];
    image?: {
      url?: string;
      alt?: string;
      mediaAlt?: string;
      blurDataUrl?: string;
    };
    placeholderImage?: string;
    backgroundImage?: string;
    showTrustBadge: boolean;
    trustBadge?: TrustBadgeType;
    displayOptions?: {
      layout: 'single' | 'grid';
      carouselDuration?: number;
    };
  };
}) {
  // Props
  const sectionId = props.data?.sectionId;
  const title = props.data?.heading;
  const buttons = props.data?.buttons;
  const subheading = props.data?.subheading;
  const image = props.data?.image;
  const images = props.data?.images;
  const displayOptions = props.data?.displayOptions;
  const oddImages = images?.filter((_, index) => index % 2 === 0);
  const evenImages = images?.filter((_, index) => index % 2 !== 0);

  const section = useRef(null);
  const isInView = useInView(section, { amount: 0.4, once: true });

  const { coordinates: mousePosition, handleMouseMove } =
    useRelativeCoordinates(section);

  return (
    <section
      id={sectionId}
      ref={section}
      className={`bg-gradient-yellow relative overflow-hidden bg-gradient-to-b ${displayOptions?.layout === 'grid' ? '' : 'pt-24 md:pt-32 xl:pt-32 2xl:pt-36'}`}
      onMouseMove={(e) => handleMouseMove(e)}
    >
      <div className='container-padding container relative mx-auto'>
        <div className='flex flex-col items-stretch gap-x-7 md:flex-row md:justify-start'>
          <div
            data-test-qa={testIds.homePageBanner}
            className={`relative z-10 flex flex-grow flex-col justify-start space-y-6 pb-9 md:w-1/2 md:pb-24 md:pt-28 xl:pb-40 2xl:pb-56 ${displayOptions?.layout === 'grid' ? 'pt-16 sm:pt-28 md:pt-28 xl:pt-32 2xl:pt-48' : ''}`}
          >
            {/* Mobile pattern  */}
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='absolute -right-12 top-36 z-0 h-28 w-28 sm:h-32 sm:w-32 md:hidden'
              width='110'
              height='98'
              viewBox='0 0 110 98'
              fill='none'
            >
              <path
                d='M69.8125 58.2017C70.0991 58.2017 70.3284 57.9723 70.3284 57.6854V45.5414H82.8811V57.6854C82.8811 57.9723 83.1103 58.2017 83.3969 58.2017H95.0899V70.7091H82.9575C82.6709 70.7091 82.4416 70.9386 82.4416 71.2254V83.6754H70.6341V71.2828C70.6341 70.9959 70.4048 70.7665 70.1182 70.7665H57.661V58.2017H69.7743H69.8125ZM44.1721 71.5123H56.6293V84.2874C56.6293 84.5742 56.8586 84.8037 57.1452 84.8037H69.8125V97.1963C69.8125 97.4832 70.0418 97.7127 70.3284 97.7127H83.3205C83.6071 97.7127 83.8364 97.4832 83.8364 97.1963V84.7272H95.9688C96.2553 84.7272 96.4846 84.4977 96.4846 84.2109V71.4358H108.617C108.904 71.4358 109.133 71.2063 109.133 70.9194V57.934C109.133 57.6471 108.904 57.4177 108.617 57.4177H96.9241V44.7C96.9241 44.4131 96.6948 44.1836 96.4082 44.1836H83.6453V32.0205C83.6453 31.7336 83.416 31.5042 83.1295 31.5042H70.1564C69.8698 31.5042 69.6405 31.7336 69.6405 32.0205V44.1453H56.8777C56.5911 44.1453 56.3618 44.3748 56.3618 44.6617V57.4368H44.1721C43.8855 57.4368 43.6562 57.6663 43.6562 57.9531V70.9386C43.6562 71.2254 43.8855 71.4549 44.1721 71.4549'
                fill='#D64C19'
              />
              <path
                d='M27.0144 25.5635H14.8438V14.1462H27.0144V25.5635ZM1.31665 27.3229H13.9649V39.0653C13.9649 39.3521 14.1942 39.5816 14.4807 39.5816H27.4538C27.7404 39.5816 27.9697 39.3521 27.9697 39.0653V26.3858H40.4842C40.7707 26.3858 41 26.1563 41 25.8695V12.8649C41 12.578 40.7707 12.3486 40.4842 12.3486H27.8359V0.606201C27.8359 0.319336 27.6066 0.0898438 27.32 0.0898438H14.347C14.0604 0.0898438 13.8311 0.319336 13.8311 0.606201V13.2856H1.31665C1.03005 13.2856 0.800781 13.5151 0.800781 13.802V26.7874C0.800781 27.0743 1.03005 27.3038 1.31665 27.3038'
                fill='#D64C19'
              />
            </svg>
            <h1 className='heading-xxl relative z-10 font-display font-medium tracking-tighter text-charcoal'>
              {title && (
                <PortableText
                  components={components}
                  value={title}
                ></PortableText>
              )}
            </h1>
            <h2 className='heading-xs max-w-lg pr-4 text-charcoal sm:pr-0 md:pr-5 lg:pr-10 xl:pr-0 [&_strong]:text-pink'>
              {subheading && (
                <PortableText
                  components={components}
                  value={subheading}
                ></PortableText>
              )}
            </h2>
            <div
              className={`flex items-center gap-6 md:pt-6 ${displayOptions?.layout === 'grid' ? 'pb-10' : ''}`}
            >
              {buttons &&
                buttons.length > 0 &&
                buttons.map((button, index) => (
                  <IKButton
                    key={`home-banner-button-${index}`}
                    link={button.link}
                    color='charcoal'
                    variant={index === 0 ? 'filled' : 'text'}
                    size={index === 0 ? 'md' : 'sm'}
                    className={`flex items-center gap-1 ${
                      index === 0
                        ? ''
                        : 'bg-gradient-line relative gap-2 overflow-x-hidden !rounded-none bg-right-bottom bg-no-repeat !py-2 transition-all duration-500 hover:!bg-left-bottom hover:transition-all hover:duration-500'
                    }`}
                  >
                    <span>{button.text}</span>
                    {index === 0 && (
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='25'
                        height='25'
                        viewBox='0 0 25 25'
                        fill='none'
                      >
                        <path
                          d='M17.5854 12.5703L10.0854 20.0704C10.0157 20.14 9.93302 20.1953 9.84197 20.233C9.75093 20.2707 9.65335 20.2901 9.5548 20.2901C9.45625 20.2901 9.35867 20.2707 9.26763 20.233C9.17658 20.1953 9.09386 20.14 9.02417 20.0704C8.95449 20.0007 8.89922 19.9179 8.8615 19.8269C8.82379 19.7359 8.80438 19.6383 8.80438 19.5397C8.80438 19.4412 8.82379 19.3436 8.8615 19.2526C8.89922 19.1615 8.95449 19.0788 9.02417 19.0091L15.9945 12.0397L9.02417 5.07035C8.88344 4.92962 8.80438 4.73875 8.80438 4.53972C8.80438 4.3407 8.88344 4.14983 9.02417 4.0091C9.1649 3.86837 9.35578 3.78931 9.5548 3.78931C9.75382 3.78931 9.94469 3.86837 10.0854 4.0091L17.5854 11.5091C17.6552 11.5788 17.7105 11.6615 17.7482 11.7525C17.786 11.8436 17.8054 11.9412 17.8054 12.0397C17.8054 12.1383 17.786 12.2359 17.7482 12.3269C17.7105 12.418 17.6552 12.5007 17.5854 12.5703Z'
                          fill='currentColor'
                        />
                      </svg>
                    )}
                    {button.link.url === 'shop' && (
                      <ShoppingCart weight='fill' className='text-2xl' />
                    )}
                  </IKButton>
                ))}
            </div>

            <TrustBadge
              trustBadge={props?.data?.trustBadge}
              showTrustBadge={props?.data?.showTrustBadge}
            />
          </div>
          <div
            className={`relative hidden flex-grow sm:block md:w-1/2 ${displayOptions?.layout === 'grid' ? '' : 'xl:pl-16'}`}
          >
            {displayOptions?.layout !== 'grid' && (
              <div className='relative -mb-16 h-full max-h-[700px] w-full md:max-h-full'>
                {/* Floating patterns */}
                <SVGPatterns
                  isInView={isInView}
                  mousePosition={mousePosition}
                />
                {image && image.url && (
                  <Image
                    quality={95}
                    priority={true}
                    alt={image.mediaAlt || image.alt || 'iKhokha'}
                    width='655'
                    height='740'
                    loading='eager'
                    sizes='(max-width: 768px) 95vw, (max-width: 1024px) 50vw, 655px'
                    placeholder='blur'
                    blurDataURL={image.blurDataUrl || ''}
                    src={image.url}
                    className='relative z-10 h-full w-full rounded-t-[20px] object-cover shadow-[-2px_21px_31px_#DEA403]'
                  />
                )}
              </div>
            )}
            {displayOptions?.layout === 'grid' &&
              oddImages &&
              oddImages.length > 0 &&
              evenImages &&
              evenImages.length > 0 && (
                <>
                  <div className='absolute left-3 top-28 lg:left-0 lg:top-36'>
                    {/* Floating patterns */}
                    <SVGPatterns
                      isInView={isInView}
                      mousePosition={mousePosition}
                    />
                  </div>
                  <div className='relative left-0 -ml-5 -mr-20 mt-16 flex h-full w-[140%] gap-5 overflow-hidden pl-8 sm:mt-20 sm:w-[max(40vw,115%)] md:w-[max(40vw,105%)] md:gap-5 md:pr-8 lg:gap-8 xl:gap-10 2xl:mt-20 2xl:gap-14'>
                    <div className='absolute -top-20 left-0 right-0 z-10 mx-auto h-[90px] w-[85%] bg-charcoal/30 blur-lg'></div>
                    <InfiniteCarousel
                      duration={displayOptions?.carouselDuration ?? 50}
                      direction='up'
                      images={oddImages}
                    />
                    <InfiniteCarousel
                      duration={
                        displayOptions?.carouselDuration
                          ? displayOptions?.carouselDuration * 0.7
                          : 40
                      }
                      startingPosition={40}
                      direction='up'
                      images={evenImages}
                    />
                  </div>
                </>
              )}
          </div>
        </div>

        {!props?.data?.showTrustBadge ? (
          <m.div
            initial='hidden'
            variants={fadeUp}
            transition={{
              delay: mousePosition.x ? 0 : 0.3,
              duration: 0.3,
              type: 'tween',
            }}
            className={`absolute -bottom-14 -left-10 z-0 hidden h-48 w-40 opacity-20 xl:block 2xl:opacity-100`}
            animate={{
              ...(isInView
                ? {
                    opacity: 1,
                    x: mousePosition.x ? mousePosition.x / 80 : 0,
                    y: mousePosition.y ? mousePosition.y / 80 : 0,
                  }
                : fadeUp.hidden),
            }}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              className='w-full'
              width='170'
              height='172'
              viewBox='0 0 170 172'
              fill='none'
            >
              <path
                d='M142.717 89.9211H155.025V101.863H142.717V89.9211ZM129.816 103.501H142.123V115.115H129.816V103.501ZM116.259 116.734H128.566V129.146H116.259V116.734ZM88.5098 144.017H100.818V157.106H88.5098V144.017ZM75.0348 129.7H87.6087V143.157H75.0348V129.7ZM61.6211 116.447H73.9289V128.86H61.6211V116.447ZM48.5557 104.034H60.3105V114.829H48.5557V104.034ZM59.9419 89.1427H48.187V76.9143H59.9419V89.1427ZM73.5603 75.2756H61.2525V62.8628H73.5603V75.2756ZM87.2401 62.0229H74.6662V48.5655H87.2401V62.0229ZM100.449 47.7052H88.1412V34.6164H100.449V47.7052ZM114.416 61.7362H102.108V48.5655H114.416V61.7362ZM128.198 74.9888H115.89V62.576H128.198V74.9888ZM115.501 101.822C115.501 101.822 115.358 101.883 115.276 101.924V89.9006H127.85V101.842H115.501V101.822ZM102.374 115.075V103.747H114.723C114.723 103.747 114.866 103.686 114.948 103.645V115.075H102.374ZM75.1782 103.747H87.4859V114.808H75.1782V103.747ZM87.1173 88.856H74.8095V76.8938H87.1173V88.856ZM88.0798 90.1464H100.388V101.535H88.0798V90.1464ZM114.559 88.856H101.985V76.6275H114.559V88.856ZM101.002 75.2756H88.4279V62.5965H101.002V75.2756ZM61.5187 90.1669H73.1917V101.556H61.5187V90.1669ZM88.7965 116.447H101.371V129.126H88.7965V116.447ZM141.755 88.5897H129.447V76.6275H141.755V88.5897ZM34.4048 90.0645H46.6102V101.658H34.4048V90.0645ZM114.784 143.157H102.476V129.986H114.784V143.157ZM169.36 103.481C169.667 103.481 169.913 103.235 169.913 102.928V89.1427C169.913 88.8355 169.667 88.5897 169.36 88.5897H156.643V75.5624C156.643 75.2551 156.397 75.0093 156.09 75.0093H143.086V62.3097C143.086 62.0025 142.84 61.7567 142.533 61.7567H129.304V48.2787C129.304 47.9715 129.058 47.7257 128.751 47.7257H115.337V34.2477C115.337 33.9405 115.091 33.6947 114.784 33.6947H101.985V20.2986C101.985 19.9914 101.739 19.7456 101.432 19.7456H87.6702C87.363 19.7456 87.1173 19.9914 87.1173 20.2986V33.6947H73.8265C73.5193 33.6947 73.2736 33.9405 73.2736 34.2477V47.992H60.3514C60.0442 47.992 59.7986 48.2378 59.7986 48.545V62.0229H46.9378C46.6306 62.0229 46.3848 62.2688 46.3848 62.576V75.1732H33.8723C33.5651 75.1732 33.3195 75.419 33.3195 75.7262V89.3066H20.1311C19.8239 89.3066 19.5781 89.5524 19.5781 89.8597V103.645C19.5781 103.952 19.8239 104.198 20.1311 104.198H33.6881V115.976C33.6881 116.283 33.9337 116.529 34.2409 116.529H46.7536V129.126C46.7536 129.433 46.9992 129.679 47.3064 129.679H60.1672V143.157C60.1672 143.464 60.4128 143.71 60.72 143.71H73.6423V157.454C73.6423 157.762 73.8879 158.007 74.1951 158.007H87.4859V171.403C87.4859 171.711 87.7316 171.956 88.0388 171.956H101.821C102.128 171.956 102.374 171.711 102.374 171.403V158.007H115.173C115.48 158.007 115.726 157.762 115.726 157.454V143.976H129.14C129.447 143.976 129.693 143.731 129.693 143.423V129.945H142.922C143.229 129.945 143.475 129.699 143.475 129.392V116.693H156.479C156.786 116.693 157.032 116.447 157.032 116.14V103.461H169.381L169.36 103.481Z'
                fill='#D64C19'
              />
              <path
                d='M26.2136 25.4736H14.043V14.0564H26.2136V25.4736ZM0.515865 27.2331H13.1641V38.9754C13.1641 39.2623 13.3934 39.4918 13.68 39.4918H26.653C26.9396 39.4918 27.1689 39.2623 27.1689 38.9754V26.296H39.6834C39.97 26.296 40.1992 26.0665 40.1992 25.7796V12.7751C40.1992 12.4882 39.97 12.2587 39.6834 12.2587H27.0351V0.516358C27.0351 0.229492 26.8059 0 26.5193 0H13.5462C13.2596 0 13.0304 0.229492 13.0304 0.516358V13.1958H0.515865C0.229273 13.1958 0 13.4253 0 13.7122V26.6976C0 26.9845 0.229273 27.214 0.515865 27.214'
                fill='#D64C19'
              />
            </svg>
          </m.div>
        ) : null}
      </div>
    </section>
  );
}
